import { geoDist } from './geo';
import { I18N } from './i18n';
import { tl } from './terms';

export const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export function newReport(lang, n, lat = 46.06, lon = 11.12, zoom = 18) {
  return {
    id: uid('rep'),
    title: (photoMode() ? tl(lang).fxRepName : tl(lang).defaultRepName) + n,
    text: '',
    lat, lon, zoom,
    overlayAngle: 0,
    overlayScale: 100,
    overlayOpacity: 75,
    photos: [],
    locked: false,
    updatedAt: Date.now(),
  };
}

export function newProject(lang, center, mode = 'project', n = 0) {
  const t = tl(lang);
  const now = Date.now();
  return {
    id: uid('proj'),
    title: mode === 'single' && photoMode() ? `${t.fxRepName}${new Date().toLocaleString(t.locale)}` : mode === 'single' ? t.defaultRepName + (n && t.defaultRepName !== I18N[lang]?.defaultRepName ? n : new Date().toLocaleString(t.locale)) : t.defaultProjName + new Date().toLocaleDateString(t.locale),
    mode,
    description: '',
    lat: center?.lat ?? 46.06,
    lon: center?.lng ?? 11.12,
    createdAt: now,
    updatedAt: now,
    reports: [newReport(lang, 1, center?.lat, center?.lng, center?.zoom)],
    pipes: [],
    fittings: [],
  };
}

export function ensureProjectHasReports(proj, lang) {
  if (!Array.isArray(proj.pipes)) proj.pipes = [];
  if (!Array.isArray(proj.fittings)) proj.fittings = [];
  if (!proj.mode) proj.mode = 'project';
  if (proj.lat == null && proj.reports?.length) { proj.lat = proj.reports[0].lat; proj.lon = proj.reports[0].lon; }
  proj.pipes = proj.pipes.map((pp) => ({
    ...pp,
    points: pp.points.map((pt) => (typeof pt === 'string' ? (proj.reports || []).find((r) => r.id === pt) : pt))
      .filter(Boolean).map((pt) => (Array.isArray(pt) || pt.fid ? pt : [pt.lat, pt.lon])),
  }));
  if (!proj.reports || proj.reports.length === 0) {
    const r = newReport(lang, 1, proj.lat || 46.06, proj.lon || 11.12, proj.zoom || 18);
    proj.reports = [{
      ...r,
      text: proj.text || '',
      overlayAngle: proj.overlayAngle || 0,
      overlayScale: proj.overlayScale || 100,
      overlayOpacity: proj.overlayOpacity || 75,
      photos: proj.photos || [],
    }];
  }
  return proj;
}

export const PIPE_TYPES = {
  water: '#1d6fe8',
  sewer: '#8b5a2b',
  gas: '#e6b800',
  fire: '#d62828',
  power: '#ef4444',
  telecom: '#8b5cf6',
  heat: '#be123c',
  other: '#ff6a13',
  leak: '#dc2626',
};

// minimum cover (m) per utility – typical guide values, editable per project (project.minCover)
export const MIN_COVER = { water: 1.2, gas: 0.8, power: 0.6, telecom: 0.6, heat: 0.6, sewer: 1.0, fire: 1.2 };
export const minCoverOf = (project, p) => {
  if (/^g\d/.test(p.catId || '')) return null;
  const o = project?.minCover || {};
  if (p.type === 'power' && ['ms', 'hs'].includes(p.sub)) return +(o.power_ms ?? 0.8);
  return o[p.type] != null ? +o[p.type] : MIN_COVER[p.type] ?? null;
};
export const tooFlat = (project, p) => { const d = p.depth == null || p.depth === '' ? null : +String(p.depth).replace(',', '.'); const m = minCoverOf(project, p); return d != null && d > 0 && m != null && d < m ? m : null; };

export const CABLE_SUBS = { power: ['ns', 'ms', 'hs', 'light', 'earth'], telecom: ['fiber', 'copper', 'signal', 'duct'], heat: ['vl', 'rl', 'dual'] };

export const haversine = (a, b) => geoDist(a, b);

export const FITTING_TYPES = {
  leak: '#dc2626',
  hydrant: '#d62828',
  valve: '#1d6fe8',
  manhole: '#4b5563',
  house: '#15803d',
  air: '#0891b2',
  drain: '#7c3aed',
  cross4: '#1d4ed8',
  tee3: '#1d4ed8',
  meter: '#0891b2',
  gg: '#a16207',
  gk: '#475569',
  cjoint: '#ef4444',
  cabinet: '#8b5cf6',
  kks: '#0d9488',
  xbear: '#0f766e',
  cfault: '#b91c1c',
  locate: '#ca8a04',
  tracer: '#7c3aed',
  gmic: '#db2777',
  sleeve: '#b45309',
  lws: '#ea580c',
  gully: '#0369a1',
  dome: '#0ea5e9',
  vent: '#64748b',
  source: '#0369a1',
  prv: '#9333ea',
  pump: '#0f766e',
  node: '#334155',
  other: '#ff6a13',
};

export const FIT_STATES = { none: '', open: '#16a34a', closed: '#111827', partial: '#f59e0b', ok: '#22c55e', defect: '#dc2626', check: '#eab308' };

export function resolvePt(pt, fittings) {
  if (Array.isArray(pt)) return pt;
  const f = fittings.find((x) => x.id === pt.fid);
  return f ? [f.lat, f.lon] : null;
}

export const pipeLatLngs = (pipe, fittings = []) => pipe.points.map((pt) => resolvePt(pt, fittings)).filter(Boolean);

// identical or closer than 1 cm (no zero-length segments)
export const samePt = (a, b) => (Array.isArray(a) && Array.isArray(b) ? geoDist(a, b) < 0.01 : a?.fid && a.fid === b?.fid);

export const pathLength = (pts) => pts.reduce((s, p, i) => (i ? s + haversine(pts[i - 1], p) : 0), 0);

export const isLocked = (r) => r?.locked !== false;

// photo branch: neutral names instead of construction site
const photoMode = () => { try { return JSON.parse(localStorage.getItem('giss_profiles'))?.[0] === 'photo'; } catch { return false; } };
export const mainPhotoOf = (r) => r?.photos?.find((p) => p.role === 'main' && p.kind !== 'video') || null;

export function fitBox(w, h, max = 260) {
  const k = Math.min(1, max / Math.max(w || max, h || max));
  return { w: (w || max) * k, h: (h || max) * k };
}

// free rotation (degrees, clockwise); canvas grows to the rotated bounding box, white corners
export function rotateFree(data, deg, quality = 0.9) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const a = (deg * Math.PI) / 180, s = Math.abs(Math.sin(a)), co = Math.abs(Math.cos(a));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * co + img.height * s); c.height = Math.round(img.width * s + img.height * co);
      const cx = c.getContext('2d');
      cx.fillStyle = '#fff'; cx.fillRect(0, 0, c.width, c.height);
      cx.translate(c.width / 2, c.height / 2); cx.rotate(a); cx.drawImage(img, -img.width / 2, -img.height / 2);
      resolve({ data: c.toDataURL('image/jpeg', quality), w: c.width, h: c.height });
    };
    img.onerror = reject;
    img.src = data;
  });
}

export function rotateDataUrl(data, quality = 0.9) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.height; c.height = img.width;
      const cx = c.getContext('2d');
      cx.translate(c.width, 0); cx.rotate(Math.PI / 2); cx.drawImage(img, 0, 0);
      resolve({ data: c.toDataURL('image/jpeg', quality), w: c.width, h: c.height });
    };
    img.onerror = reject;
    img.src = data;
  });
}

export function compressImage(file, maxDim = 1600, quality = 0.82, bg = null) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const k = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * k);
        const h = Math.round(img.height * k);
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const cx = c.getContext('2d');
        if (bg) { cx.fillStyle = bg; cx.fillRect(0, 0, w, h); }
        cx.drawImage(img, 0, 0, w, h);
        resolve({ data: c.toDataURL('image/jpeg', quality), w, h });
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
